/**
 * Category Cockpit — formatting helpers and the vocabulary shared by the four pages.
 * Pure functions, unit-tested; the derivations themselves live in the backend
 * (backend-merged/services/cockpit/categoryDerive.js) so the two never disagree.
 */
import type { Aspect, BlastSide, ChapterId, SiteCell, ActionState } from '@/services/categoryCockpitService';

export const fmt = (n: number | string | null | undefined): string => (n == null || n === '' ? '—' : Number(n).toLocaleString('en-US'));
export const pct = (a: number | null | undefined, b: number | null | undefined): string => (b ? `${Math.round((100 * (a || 0)) / b)} %` : '—');
/**
 * A research cost that was never measured is not a cost of zero.
 *
 * `category_characteristics.research_cost_usd` is nullable and the characteristics API preserves
 * the null, but `Number(v || 0).toFixed(3)` turned it into "$0.000" — a free research run, which
 * reads as a measured fact (codex-reviewer, PR #6457). A genuine zero still prints as $0.000, and
 * there are 619 of those in production against 0 nulls today (db2, 2026-09-22), so the two cases
 * have to stay distinguishable on screen.
 */
export const cost = (v: number | string | null | undefined): string => (v == null || v === '' ? 'unmeasured' : `$${Number(v).toFixed(3)}`);

export const day = (s: string | null | undefined): string => (s ? String(s).slice(0, 10) : '—');
export const daysAgo = (s: string | null | undefined, now: number = Date.now()): number | null => (s ? Math.round((now - new Date(s).getTime()) / 864e5) : null);
export const leaf = (p: string): string => p.split(' > ').slice(-1)[0];
export const parent = (p: string): string => p.split(' > ').slice(0, -1).join(' > ');
export const plural = (n: number | string, w: string): string => `${n} ${w}${Number(n) === 1 ? '' : 's'}`;
export const sum = <T,>(arr: T[] | null | undefined, f: (x: T) => number | null | undefined): number => (arr || []).reduce((s, x) => s + (Number(f(x)) || 0), 0);

/**
 * Must stay identical to `OPEN_GAP_STATUSES` in backend-merged/services/cockpit/categoryDerive.js.
 * `modified` is an operator's own edit of a suggestion and is as open as `approved` — the backend
 * counts it and this list did not, so a gap an operator had just corrected vanished from every
 * count on the page (codex, PR #6457).
 */
const OPEN = new Set(['pending', 'suggested', 'approved', 'modified', 'needs_expert']);
export const gapsOpen = (s: SiteCell): number => (s.gaps || []).filter((g) => OPEN.has(g.status)).reduce((a, g) => a + (Number(g.n) || 0), 0);

/**
 * The site rows that carry ANY evidence for the path.
 *
 * Must stay identical to `sitesOf` in backend-merged/services/cockpit/categoryDerive.js. Published
 * pages and stale hints count: without them a site whose catalogue rows had moved to another path
 * disappeared here while the Outcomes chapter still showed its 11 published pages
 * (codex-reviewer-2, PR #6457).
 */
export const sitesOf = (sites: SiteCell[]): SiteCell[] => (sites || []).filter((s) => s.products || s.tier
  || s.spec_hints || gapsOpen(s) || s.products_in_children || s.pages_published || s.spec_hints_stale);

export const sinceLabel = (s: string | null | undefined, now?: number): string => {
  const d = daysAgo(s, now);
  if (d == null) return 'never';
  if (d <= 0) return 'today';
  return `${d} d ago`;
};

export const CHAPTERS: ChapterId[] = ['sector', 'identity', 'aspects', 'schema', 'contrast', 'outcomes', 'readiness', 'demand', 'intent', 'market', 'news', 'docs', 'experts', 'relations'];

export const CH_TITLES: Record<ChapterId, string> = {
  sector: 'Sector context', identity: 'Identity', aspects: 'Core aspects', schema: 'Schema', contrast: 'Catalog contrast',
  outcomes: 'Approvals & rejections', readiness: 'Readiness', demand: 'Demand signals', intent: 'Intent signals', market: 'Market data',
  news: 'Latest news', docs: 'Documents & facets', experts: 'Experts & unknowns', relations: 'Relations',
};

export const ASPECT_LABEL: Record<Aspect, string> = {
  product_types: 'Product types', personas: 'Personas', use_cases: 'Use cases', brand_tiers: 'Brand tiers', spec_semantics: 'Spec semantics',
  validation_rules: 'Validation rules', premium_features: 'Premium features', price_tiers: 'Price tiers', measurement_units: 'Measurement units', tier_vocabulary: 'Tier vocabulary',
};

export const ASPECT_DESC: Record<Aspect, string> = {
  product_types: 'Named sub-types of the category with keywords, personas and brands — used to classify products at enrichment and to name them in generated copy.',
  personas: 'Named buyer archetypes with needs, budget and language variants — drive the "who is it for" and "best for" sections and the benefit weights.',
  use_cases: 'Use cases with regex patterns per language — matched against product text at enrichment, named in generated copy.',
  brand_tiers: 'Brands grouped in entry / mid / premium — today from model memory, without citation or date per brand.',
  spec_semantics: 'What the values of each key attribute mean for a buyer: ranges, buyer fit, messaging, words to avoid.',
  validation_rules: 'Attribute equivalences and validation rules — where semantic-gap Apply writes the mappings.',
  premium_features: 'Features that mark a premium product, with the keywords that reveal them.',
  price_tiers: 'Price bands of the category — the prompt drops the sample, so this is model memory.',
  measurement_units: 'The units a buyer expects per attribute, and their conversions.',
  tier_vocabulary: 'The words that place a product in a tier (entry, mid, premium) in each language.',
};

/** chapter status class (ok · stale · bad · empty · off) → the shared state pill */
export const STATE_MAP: Record<string, ActionState> = { ok: 'ready', stale: 'attention', bad: 'blocked', empty: 'todo', off: 'off' };

export const catUrl = (path: string, ch?: ChapterId): string => `/category-cockpit/category?path=${encodeURIComponent(path)}${ch ? `&ch=${ch}` : ''}`;

/** L1 / L3 / L5 gates of the readiness scorer (category_readiness_scores), as the prototype states them */
export const GATES = { l1: 0.8, l3: 0.7, l5: 0.6 } as const;

export const gateOf = (s: SiteCell): 'L1' | 'L3' | 'L5' | 'types' => {
  if (Number(s.l1_score) < GATES.l1) return 'L1';
  if (Number(s.l3_score) < GATES.l3) return 'L3';
  if (Number(s.l5_score) < GATES.l5) return 'L5';
  return 'types';
};

/** who a decision waits on, from its free-text `who` — the first question a maintainer asks */
export const whoOf = (who: string | null | undefined): 'me' | 'jaume' | 'expert' | 'system' => {
  const w = (who || '').toLowerCase();
  if (/expert/.test(w)) return 'expert';
  if (/jaume/.test(w)) return 'jaume';
  if (/worker|studio|automatic/.test(w)) return 'system';
  return 'me';
};
export const WHO_LABEL: Record<ReturnType<typeof whoOf>, string> = { me: 'For an operator', jaume: 'For Jaume', expert: 'For an expert', system: 'For a process' };

/**
 * How far a change to this category reaches — the sentence the category header shows, and the same
 * sentence every write confirmation shows.
 *
 * It is one function and not two strings because the two must not drift: an operator who reads
 * "reaches 3 sites" in the header and then confirms a write that says something else has no way to
 * know which of the two is the measurement.
 *
 * The two sides answer different questions, and the difference is the table's key:
 *   - `knowledge` — `category_characteristics` is keyed (sector_id, category_path), so the ten
 *     aspects reach only the sites of THIS sector that carry the path;
 *   - `schema` — `canonical_attributes` is keyed by path alone, so an attribute change reaches
 *     every sector carrying it, including sites the operator is not looking at.
 */
export const reachSentence = (b: BlastSide, what: 'knowledge' | 'schema', sectorName: string): string => (what === 'knowledge'
  ? `${plural(b.sites, 'site')} · ${fmt(b.products)} products · ${fmt(b.pages)} published pages in ${sectorName}`
  : `${plural(b.sites, 'site')} in ${plural(b.sectors, 'sector')} · ${fmt(b.products)} products${b.others.length ? ` — also ${b.others.map((o) => `${o.site_name} (${o.sector || o.sector_id})`).join(', ')}` : ''}`);
